import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertOctagon,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  Loader2,
  RefreshCw,
  Search,
  ShieldAlert,
} from 'lucide-react';
import PreDispatchDeliveryRow from './PreDispatchDeliveryRow';
import {
  apiErrorMessage,
  fetchPreDispatchIntelligence,
} from './operationalIntelligenceApi';

const LEVEL_OPTIONS = [
  ['', 'Todos los niveles'],
  ['HIGH_ATTENTION', 'Alta atención'],
  ['REVIEW_RECOMMENDED', 'Revisión recomendada'],
  ['ELEVATED', 'Elevado'],
  ['WAITING_RESPONSE', 'Esperando respuesta'],
  ['CUSTOMER_REPLIED', 'Cliente respondió'],
  ['NORMAL', 'Normal'],
];

function peruToday() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Lima',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function matchesSearch(item, search) {
  const normalized = search.trim().toLocaleLowerCase('es-PE');
  if (!normalized) return true;
  const delivery = item.delivery || {};
  return [
    item.delivery_point_id,
    delivery.recipient_name,
    delivery.recipient_phone,
    delivery.seller_username,
  ].some((value) => String(value || '').toLocaleLowerCase('es-PE').includes(normalized));
}

export default function PreDispatchIntelligenceQueue({
  onOpenContext,
  onToast,
  refreshSignal = 0,
}) {
  const [operationalDate, setOperationalDate] = useState(peruToday);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [search, setSearch] = useState('');

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await fetchPreDispatchIntelligence({ date: operationalDate }));
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'No se pudo cargar la cola de pre-despacho'));
    } finally {
      setLoading(false);
    }
  }, [operationalDate]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue, refreshSignal]);

  const accountOptions = useMemo(() => {
    const accounts = new Map();
    (data?.items || []).forEach((item) => {
      const userId = item.delivery?.user_id;
      if (userId != null) {
        accounts.set(String(userId), item.delivery?.seller_username || item.account_history?.username || `Cuenta ${userId}`);
      }
    });
    return [...accounts.entries()].sort((left, right) => left[1].localeCompare(right[1], 'es'));
  }, [data]);

  const filteredItems = useMemo(() => (
    (data?.items || []).filter((item) => {
      if (accountFilter && String(item.delivery?.user_id) !== accountFilter) return false;
      if (levelFilter === 'CUSTOMER_REPLIED') {
        if (item.communication?.state !== 'CUSTOMER_REPLIED') return false;
      } else if (levelFilter && item.readiness?.level !== levelFilter) {
        return false;
      }
      return matchesSearch(item, search);
    })
  ), [accountFilter, data, levelFilter, search]);

  async function handleReviewed(result) {
    onToast?.(`Revisión de entrega #${result.delivery_point_id} registrada.`);
    await loadQueue();
  }

  const summary = data?.summary || {};

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col justify-between gap-5 border-b border-slate-200 bg-gradient-to-r from-slate-950 to-slate-800 px-5 py-5 text-white sm:px-6 lg:flex-row lg:items-end">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-pink-300">PRE-DESPACHO</p>
            <h2 className="mt-1 text-2xl font-black">Entregas que requieren contexto antes de liberar al conductor</h2>
            <p className="mt-2 max-w-3xl text-sm text-slate-300">
              Reglas explicables sobre cancelación, cuenta y comunicación. La cola recomienda revisión; nunca ejecuta acciones automáticamente.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-slate-300">
            <span className="rounded-full bg-white/10 px-3 py-1.5">
              Ventana de respuesta: {data?.waiting_minutes == null ? '—' : `${data.waiting_minutes} min`}
            </span>
            <span className="rounded-full bg-white/10 px-3 py-1.5">
              Regla: {data?.rule_version || 'dispatch_readiness_v1'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-px bg-slate-200 md:grid-cols-5">
          <SummaryMetric icon={AlertOctagon} label="Alta atención" value={summary.high_attention || 0} tone="red" />
          <SummaryMetric icon={ShieldAlert} label="Revisión recomendada" value={summary.review_recommended || 0} tone="orange" />
          <SummaryMetric icon={Clock3} label="Esperando respuesta" value={summary.waiting_response || 0} tone="blue" />
          <SummaryMetric icon={CheckCircle2} label="Cliente respondió" value={summary.customer_replied || 0} tone="emerald" />
          <SummaryMetric icon={ClipboardCheck} label="Total evaluadas" value={summary.total_candidates || 0} tone="slate" />
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[180px_minmax(190px,1fr)_minmax(210px,1fr)_minmax(260px,1.5fr)_auto]">
          <label className="text-xs font-extrabold uppercase tracking-wide text-slate-500">
            Fecha
            <input
              type="date"
              value={operationalDate}
              onChange={(event) => setOperationalDate(event.target.value)}
              className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-bold normal-case tracking-normal text-slate-800 outline-none focus:border-brand-primary"
            />
          </label>
          <label className="text-xs font-extrabold uppercase tracking-wide text-slate-500">
            Cuenta
            <select
              value={accountFilter}
              onChange={(event) => setAccountFilter(event.target.value)}
              className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold normal-case tracking-normal text-slate-800 outline-none focus:border-brand-primary"
            >
              <option value="">Todas las cuentas</option>
              {accountOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="text-xs font-extrabold uppercase tracking-wide text-slate-500">
            Nivel
            <select
              value={levelFilter}
              onChange={(event) => setLevelFilter(event.target.value)}
              className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold normal-case tracking-normal text-slate-800 outline-none focus:border-brand-primary"
            >
              {LEVEL_OPTIONS.map(([value, label]) => <option key={value || 'all'} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="text-xs font-extrabold uppercase tracking-wide text-slate-500">
            Buscar destinatario / teléfono
            <span className="relative mt-1.5 block">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Nombre, teléfono o ID"
                className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm font-medium normal-case tracking-normal text-slate-800 outline-none placeholder:text-slate-400 focus:border-brand-primary"
              />
            </span>
          </label>
          <button
            type="button"
            onClick={loadQueue}
            disabled={loading}
            className="mt-auto flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-extrabold text-white hover:bg-slate-800 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4 text-xs text-slate-500">
          <p><strong className="text-slate-800">{filteredItems.length}</strong> de {data?.items?.length || 0} candidatas visibles</p>
          <p>Orden: alta atención → revisión → elevado → espera → normal; sin respuesta más antigua primero.</p>
        </div>
      </section>

      {error && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-800">
          <div>
            <p className="font-extrabold">No se pudo cargar pre-despacho</p>
            <p className="text-sm">{error}</p>
          </div>
          <button type="button" onClick={loadQueue} className="rounded-lg bg-red-700 px-4 py-2 text-sm font-bold text-white">Reintentar</button>
        </div>
      )}

      {loading && !data ? (
        <div className="flex min-h-64 items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm">
          <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
          Evaluando candidatas con verdad operacional...
        </div>
      ) : filteredItems.length ? (
        <section className="space-y-4" aria-label="Cola de inteligencia de pre-despacho">
          {filteredItems.map((item) => (
            <PreDispatchDeliveryRow
              key={item.delivery_point_id}
              item={item}
              onOpenContext={onOpenContext}
              onReviewed={handleReviewed}
            />
          ))}
        </section>
      ) : !error && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-12 text-center text-emerald-900">
          <CheckCircle2 className="mx-auto h-9 w-9 text-emerald-600" />
          <p className="mt-3 text-lg font-extrabold">No hay candidatas para los filtros seleccionados</p>
          <p className="mt-1 text-sm text-emerald-800">Las entregas terminales o con evidencia física de despacho no entran en esta cola.</p>
        </div>
      )}
    </div>
  );
}

function SummaryMetric({ icon: Icon, label, value, tone }) {
  const tones = {
    red: 'bg-red-100 text-red-700',
    orange: 'bg-orange-100 text-orange-700',
    blue: 'bg-blue-100 text-blue-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    slate: 'bg-slate-200 text-slate-700',
  };
  return (
    <div className="bg-white p-4 sm:p-5">
      <div className={`inline-flex rounded-lg p-2 ${tones[tone] || tones.slate}`}><Icon className="h-4 w-4" /></div>
      <p className="mt-2 text-[11px] font-extrabold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-black text-slate-900">{value}</p>
    </div>
  );
}
